import React from 'react';
import { useNavigate } from 'react-router-dom';

const ErrorHandler = ({ 
  error, 
  onRetry, 
  onGoHome, 
  sessionId = null, 
  showRetry = true,
  customActions = null 
}) => {
  const navigate = useNavigate();

  const getErrorInfo = (error) => {
    switch (error.type) {
      case 'SESSION_NOT_FOUND':
        return {
          icon: '🔍',
          title: 'Session introuvable',
          message: `La session "${error.sessionId || sessionId || 'demandée'}" n'existe pas ou a été supprimée.`,
          details: 'Vérifiez le code de session ou créez une nouvelle session.',
          actions: [
            { text: '🏠 Retour à l\'accueil', action: () => navigate('/'), primary: true },
            { text: '📋 Voir les sessions', action: () => navigate('/listesessions'), secondary: true }
          ]
        };

      case 'SESSION_EXPIRED':
        return {
          icon: '⏰',
          title: 'Session expirée',
          message: 'Cette session a expiré et n\'est plus accessible.',
          details: 'Les sessions inactives sont automatiquement supprimées après 48 heures.',
          actions: [
            { text: '🏠 Retour à l\'accueil', action: () => navigate('/'), primary: true },
            { text: '✨ Créer une nouvelle session', action: () => navigate('/nouvellesession'), secondary: true }
          ]
        };

      case 'USER_BANNED':
        return {
          icon: '🚫',
          title: 'Accès refusé',
          message: 'Vous avez été exclu de cette session.',
          details: 'Contactez l\'organisateur si vous pensez qu\'il s\'agit d\'une erreur.',
          actions: [
            { text: '🏠 Retour à l\'accueil', action: () => navigate('/'), primary: true }
          ]
        };

      case 'PASSWORD_REQUIRED':
        return {
          icon: '🔒',
          title: 'Mot de passe requis',
          message: 'Cette session est protégée par un mot de passe.',
          details: 'Demandez le mot de passe à l\'organisateur de la session.',
          actions: [
            { text: '🔓 Réessayer', action: onRetry, primary: true },
            { text: '🏠 Retour à l\'accueil', action: () => navigate('/'), secondary: true }
          ]
        };

      case 'PASSWORD_INCORRECT':
        return {
          icon: '❌',
          title: 'Mot de passe incorrect',
          message: 'Le mot de passe saisi ne correspond pas.',
          details: 'Vérifiez le mot de passe ou contactez l\'organisateur.',
          actions: [
            { text: '🔓 Réessayer', action: onRetry, primary: true },
            { text: '🏠 Retour à l\'accueil', action: () => navigate('/'), secondary: true }
          ]
        };

      case 'NETWORK_ERROR':
        return {
          icon: '🌐',
          title: 'Problème de connexion',
          message: 'Impossible de se connecter au serveur.',
          details: 'Vérifiez votre connexion internet.',
          actions: [
            { text: '🔄 Réessayer', action: onRetry, primary: true },
            { text: '🏠 Retour à l\'accueil', action: () => navigate('/'), secondary: true }
          ]
        };

      case 'SERVER_ERROR':
        return {
          icon: '⚠️',
          title: 'Erreur serveur',
          message: 'Le serveur rencontre des difficultés techniques.',
          details: 'Veuillez réessayer dans quelques instants.',
          actions: [
            { text: '🔄 Réessayer', action: onRetry, primary: true },
            { text: '🏠 Retour à l\'accueil', action: () => navigate('/'), secondary: true }
          ]
        };

      case 'USER_NOT_FOUND':
        return {
          icon: '👤',
          title: 'Profil utilisateur introuvable',
          message: 'Votre profil utilisateur n\'a pas pu être trouvé.',
          details: 'Un nouveau profil va être créé automatiquement.',
          actions: [
            { text: '🔄 Actualiser', action: () => window.location.reload(), primary: true }
          ]
        };

      case 'SESSION_CLOSED':
        return {
          icon: '🏁',
          title: 'Session terminée',
          message: 'Cette session s\'est terminée normalement.',
          details: 'Vous pouvez consulter les résultats finaux.',
          actions: [
            { text: '📊 Voir les résultats', action: onRetry, primary: true },
            { text: '🏠 Retour à l\'accueil', action: () => navigate('/'), secondary: true }
          ]
        };

      case 'TIMEOUT':
        return {
          icon: '⏱️',
          title: 'Délai d\'attente dépassé',
          message: 'L\'opération a pris trop de temps.',
          details: 'Le serveur met plus de temps que prévu à répondre.',
          actions: [
            { text: '🔄 Réessayer', action: onRetry, primary: true },
            { text: '🏠 Retour à l\'accueil', action: () => navigate('/'), secondary: true }
          ]
        };

      case 'VALIDATION_ERROR':
        return {
          icon: '📝',
          title: 'Données invalides',
          message: error.message || 'Les données fournies ne sont pas valides.',
          details: error.details || 'Vérifiez les informations saisies.',
          actions: [
            { text: '🔄 Réessayer', action: onRetry, primary: true },
            { text: '🏠 Retour à l\'accueil', action: () => navigate('/'), secondary: true }
          ]
        };

      case 'ENDPOINT_NOT_FOUND':
        return {
          icon: '🔗',
          title: 'Service indisponible',
          message: 'Le service demandé n\'est pas disponible.',
          details: 'Le serveur ne peut pas traiter cette demande actuellement.',
          actions: [
            { text: '🔄 Réessayer', action: onRetry, primary: true },
            { text: '🏠 Retour à l\'accueil', action: () => navigate('/'), secondary: true }
          ]
        };

      case 'FETCH_WARNING':
        return {
          icon: '⚠️',
          title: 'Problème de chargement',
          message: error.message || 'Certaines données n\'ont pas pu être chargées.',
          details: error.details || 'Vous pouvez continuer mais certaines informations peuvent être manquantes.',
          actions: [
            { text: '🔄 Réessayer', action: onRetry, primary: true },
            { text: '✓ Continuer', action: () => navigate('/'), secondary: true }
          ]
        };

      default:
        return {
          icon: '❓',
          title: 'Erreur inattendue',
          message: error.message || 'Une erreur inattendue s\'est produite.',
          details: error.details || 'Veuillez réessayer ou contacter l\'administrateur.',
          actions: [
            { text: '🔄 Réessayer', action: onRetry, primary: true },
            { text: '🏠 Retour à l\'accueil', action: () => navigate('/'), secondary: true }
          ]
        };
    }
  };

  const errorInfo = getErrorInfo(error);
  const actions = customActions || errorInfo.actions.filter(action => 
    // Filtrer les actions non disponibles
    !(action.action === onRetry && !showRetry)
  );

  return (
    <div className="fade-in">
      <div className="card text-center">
        <div className="text-6xl mb-4">{errorInfo.icon}</div>
        
        <h2 className="text-2xl font-bold text-primary mb-3">{errorInfo.title}</h2>
        
        <p className="text-lg mb-4">{errorInfo.message}</p>
        
        {errorInfo.details && (
          <div className="bg-secondary p-4 rounded-lg mb-6">
            <p className="text-sm text-secondary">{errorInfo.details}</p>
          </div>
        )}

        {sessionId && (
          <div className="bg-primary-light p-3 rounded-lg mb-6">
            <p className="text-sm">
              <strong>Session :</strong> <code className="text-primary font-mono">{sessionId}</code>
            </p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {actions.map((action, index) => (
            <button
              key={index}
              onClick={action.action}
              className={`btn ${action.primary ? 'btn-primary' : 'btn-secondary'} ${action.large ? 'btn-large' : ''}`}
            >
              {action.text}
            </button>
          ))}
        </div>

        {/* Détails techniques uniquement en développement */}
        {error.stack && process.env.NODE_ENV === 'development' && (
          <details className="mt-6 text-left">
            <summary className="cursor-pointer text-sm text-secondary">
              Détails techniques (développement)
            </summary>
            <pre className="mt-2 p-3 bg-secondary text-xs rounded overflow-auto">
              {error.stack}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};

export default ErrorHandler;